import React, { useEffect, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Platform,
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import * as KeepAwake from 'expo-keep-awake';
import { AppDispatch } from '../../redux/store';
import {
  selectCookingSession,
  advanceStep,
  previousStep,
  goToStep,
  setTimerDuration,
  startTimer,
  pauseTimer,
  tickTimers,
} from '../../redux/cookingSessionSlice';
import { Recipe } from '../../types/recipe';
import { getCookingSteps } from '../../utils/cookingSteps';
import { buildStepIngredients } from '../../utils/stepIngredientAliases';
import { isDesktopWeb } from '../../utils/platformDetection';
import { useRecipeAdjustment } from '../../hooks/useRecipeAdjustment';
import { useStepChipOrphans } from '../../hooks/useStepChipOrphans';
import { AdjustedText } from '../shared/AdjustedText';
import { AdjustedChip } from '../shared/AdjustedChip';
import { Paprika, PaprikaRadius, PaprikaShadows } from '../../styles/theme';
import { CookingTopBar } from './CookingTopBar';
import { StepIngChips } from './StepIngChips';
import { StepProgressDots } from './StepProgressDots';
import { StepRail } from './StepRail';
import { TimerCard } from './TimerCard';
import { useTranslation } from 'react-i18next';

interface Props {
  recipe: Recipe;
  /** Display servings override (meal-plan path passes meal.servings). */
  servingsOverride?: number | string | null;
  /** Disable the last-step CTA while the finish flow runs. */
  finishDisabled?: boolean;
  onQuit: () => void;
  /** Called when "next" is pressed on the last step. */
  onFinish: () => void;
}

/** Decorative beige of the giant step numerals — non-token per design handoff. */
const NUMERAL_BEIGE = '#ecdfce';

/**
 * The shared full-screen cooking step view (handoff `RD_DesktopCooking` /
 * `RD_MobileCooking`). Owns step navigation, keep-awake, and per-step timers;
 * entry points only provide quit/finish behavior.
 */
export const CookingStepScreen: React.FC<Props> = ({
  recipe,
  servingsOverride,
  finishDisabled = false,
  onQuit,
  onFinish,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const session = useSelector(selectCookingSession);
  const stepIndex = session.stepIndex;
  const desktop = isDesktopWeb();

  const { steps } = useMemo(() => getCookingSteps(recipe), [recipe]);
  const allIngredients = useMemo(() => buildStepIngredients(recipe), [recipe]);
  const stepTexts = useMemo(() => steps.map(s => s.text), [steps]);
  // An ingredient no step's chips will show is reported, never dropped in silence (#857).
  useStepChipOrphans({
    recipeId: recipe.id,
    language: i18n.language,
    stepTexts,
    ingredients: allIngredients,
    stored: recipe.stepIngredients,
    ingredientGroups: recipe.ingredientGroups,
  });

  const totalSteps = steps.length;
  const current = steps[stepIndex];
  const isLastStep = stepIndex >= totalSteps - 1;
  const stepTimer = session.timers[stepIndex];
  const servings = servingsOverride ?? recipe.servingsDisplay ?? recipe.servings;

  // Cooking mode previously received the servings only as a header label, so
  // the step text and chips below it still read the base-serving amounts in the
  // source units. Both now go through the same adjustment as the recipe page.
  const adjustment = useRecipeAdjustment(recipe, servingsOverride ?? recipe.servings);

  // Keep screen awake while this view is mounted. Only deactivate if
  // activation succeeded (prevents "not activated yet" throw on unmount).
  const keepAwakeActive = useRef(false);
  useEffect(() => {
    KeepAwake.activateKeepAwakeAsync()
      .then(() => { keepAwakeActive.current = true; })
      .catch(() => {});
    return () => {
      if (keepAwakeActive.current) {
        keepAwakeActive.current = false;
        KeepAwake.deactivateKeepAwake?.().catch(() => {});
      }
    };
  }, []);

  // Tick only while a timer is actually running — an unconditional interval
  // would dispatch (and persist) every second for the whole cooking session.
  const hasRunningTimer = Object.values(session.timers).some((t) => t.running && t.rem > 0);
  useEffect(() => {
    if (!hasRunningTimer) return;
    const id = setInterval(() => dispatch(tickTimers()), 1000);
    return () => clearInterval(id);
  }, [hasRunningTimer, dispatch]);

  // Seed the step's timer with its parsed duration — paused. The user starts it.
  useEffect(() => {
    if (session.timers[stepIndex] !== undefined) return;
    const duration = steps[stepIndex]?.durationSec;
    if (duration && duration > 0) {
      dispatch(setTimerDuration({ stepIndex, seconds: duration }));
    }
  }, [stepIndex, steps]);

  // Desktop web: arrow-key step navigation (never finishes the session).
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && !isLastStep) dispatch(advanceStep());
      else if (e.key === 'ArrowLeft') dispatch(previousStep());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isLastStep, dispatch]);

  const handleNext = () => {
    if (isLastStep) onFinish();
    else dispatch(advanceStep());
  };

  const handleTimerToggle = () => {
    const duration = current?.durationSec ?? 0;
    if (!stepTimer || stepTimer.rem === 0) {
      if (duration > 0) {
        dispatch(setTimerDuration({ stepIndex, seconds: duration }));
        dispatch(startTimer({ stepIndex }));
      }
      return;
    }
    if (stepTimer.running) dispatch(pauseTimer({ stepIndex }));
    else dispatch(startTimer({ stepIndex }));
  };

  const handleTimerReset = () => {
    const duration = current?.durationSec ?? 0;
    if (duration > 0) {
      dispatch(pauseTimer({ stepIndex }));
      dispatch(setTimerDuration({ stepIndex, seconds: duration }));
    }
  };

  const eyebrow = current
    ? `${t('cooking.stepNumber', { number: current.globalNumber })}${current.sectionName ? ' · ' + current.sectionName.toUpperCase() : ''}`
    : '';

  // Servings as numbers, so the state line can read "4 → 6 portions".
  const baseServingsNum =
    typeof recipe.servings === 'number'
      ? recipe.servings
      : parseInt(String(recipe.servings), 10) || 4;
  const servingsNum = Math.round(baseServingsNum * adjustment.ratio);

  // The transform has to stay visible while cooking — this is the screen you
  // are standing in front of the stove with. No servings reset here: the
  // servings come from the meal plan, only the unit choice is undoable.
  const adjustedLine = adjustment.isAdjusted ? (
    <View style={styles.adjustedLine}>
      <AdjustedChip
        adjustment={adjustment}
        baseServings={baseServingsNum}
        servings={servingsNum}
        variant="inline"
      />
    </View>
  ) : null;

  const railSteps = steps.map((s) => ({
    globalNumber: s.globalNumber,
    sectionName: s.sectionName,
    preview: s.text,
  }));

  if (totalSteps === 0) {
    return (
      <View style={styles.container}>
        <SafeAreaView>
          <CookingTopBar
            title={recipe.title}
            servings={servings}
            variant="quit"
            compact={!desktop}
            onLeftPress={onQuit}
          />
        </SafeAreaView>
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>{t('cooking.noSteps')}</Text>
        </View>
      </View>
    );
  }

  const stepContent = (
    <>
      <AdjustedText
        text={current?.text ?? ''}
        options={adjustment.options}
        style={[styles.stepText, desktop && styles.stepTextDesktop]}
      />
      <StepIngChips
        stepText={current?.text ?? ''}
        allIngredients={allIngredients}
        recipeStepTexts={stepTexts}
        stepIndex={stepIndex}
        storedStepIngredients={recipe.stepIngredients}
        ingredientGroups={recipe.ingredientGroups}
        big={desktop}
        options={adjustment.options}
      />
      {stepTimer ? (
        <View style={styles.timerArea}>
          <TimerCard
            remainingSec={stepTimer.rem}
            running={stepTimer.running}
            big={desktop}
            onToggle={handleTimerToggle}
            onReset={handleTimerReset}
          />
        </View>
      ) : null}
    </>
  );

  if (desktop) {
    return (
      <View style={styles.container}>
        <CookingTopBar
          title={recipe.title}
          servings={servings}
          variant="quit"
          onLeftPress={onQuit}
        />
        {adjustedLine}
        <View style={styles.desktopBody}>
          <View style={styles.mainPanel}>
            <Text style={styles.eyebrow}>{eyebrow}</Text>
            <View style={styles.stepRow}>
              <Text style={styles.numeralDesktop}>{current?.globalNumber}</Text>
              <ScrollView style={styles.stepCol} showsVerticalScrollIndicator={false}>
                {stepContent}
              </ScrollView>
            </View>
            <View style={styles.navRow}>
              <TouchableOpacity
                style={[styles.prevButton, stepIndex === 0 && styles.navDisabled]}
                onPress={() => dispatch(previousStep())}
                disabled={stepIndex === 0}
              >
                <Feather name="chevron-left" size={18} color={Paprika.textPrimary} />
                <Text style={styles.prevButtonText}>{t('cooking.previous')}</Text>
              </TouchableOpacity>
              <StepProgressDots stepIndex={stepIndex} totalSteps={totalSteps} />
              <TouchableOpacity
                style={[styles.nextButton, isLastStep && finishDisabled && styles.navDisabled]}
                onPress={handleNext}
                disabled={isLastStep && finishDisabled}
              >
                <Text style={styles.nextButtonText}>
                  {isLastStep ? t('cooking.ready') : t('cooking.nextStep')}
                </Text>
                <Feather name="chevron-right" size={18} color={Paprika.onPrimary} />
              </TouchableOpacity>
            </View>
          </View>
          <StepRail steps={railSteps} currentIndex={stepIndex} onGoToStep={(i) => dispatch(goToStep(i))} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <SafeAreaView>
        <CookingTopBar
          title={recipe.title}
          servings={servings}
          variant="quit"
          compact
          onLeftPress={onQuit}
        />
        {adjustedLine}
        <View style={styles.mobileDots}>
          <StepProgressDots stepIndex={stepIndex} totalSteps={totalSteps} />
        </View>
      </SafeAreaView>
      <ScrollView
        style={styles.mobileScroll}
        contentContainerStyle={styles.mobileScrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.mobileNumeralRow}>
          <Text style={styles.numeralMobile}>{current?.globalNumber}</Text>
          <View style={styles.mobileEyebrowBlock}>
            <Text style={styles.eyebrow}>{t('cooking.stepNumber', { number: current?.globalNumber })}</Text>
            {current?.sectionName ? (
              <Text style={styles.mobileSectionSub}>{current.sectionName}</Text>
            ) : null}
          </View>
        </View>
        {stepContent}
      </ScrollView>
      <SafeAreaView style={styles.mobileFooterSafe}>
        <View style={styles.mobileFooter}>
          <TouchableOpacity
            style={[styles.prevSquare, stepIndex === 0 && styles.navDisabled]}
            onPress={() => dispatch(previousStep())}
            disabled={stepIndex === 0}
          >
            <Feather name="chevron-left" size={19} color={Paprika.textPrimary} />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.nextButton, styles.nextButtonMobile, isLastStep && finishDisabled && styles.navDisabled]}
            onPress={handleNext}
            disabled={isLastStep && finishDisabled}
          >
            <Text style={styles.nextButtonText}>
              {isLastStep ? t('cooking.ready') : t('cooking.nextStep')}
            </Text>
            <Feather name="chevron-right" size={18} color={Paprika.onPrimary} />
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  emptyText: {
    fontSize: 14,
    fontFamily: 'Inter-Regular',
    color: Paprika.textSecondary,
    textAlign: 'center',
  },

  // Shared step content
  eyebrow: {
    fontSize: 11,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    color: Paprika.accent,
  },
  stepText: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 22,
    lineHeight: 31,
    letterSpacing: -0.2,
    color: Paprika.textPrimary,
  },
  stepTextDesktop: {
    fontSize: 30,
    lineHeight: 42,
    maxWidth: 620,
  },
  timerArea: {
    marginTop: 26,
  },
  adjustedLine: {
    paddingHorizontal: 16,
    paddingBottom: 8,
  },

  // Desktop
  desktopBody: {
    flex: 1,
    flexDirection: 'row',
    minHeight: 0,
  },
  mainPanel: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 40,
    paddingHorizontal: 56,
  },
  stepRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 28,
    marginTop: 18,
    minHeight: 0,
  },
  numeralDesktop: {
    fontFamily: 'Newsreader-Italic',
    fontStyle: 'italic',
    fontSize: 120,
    lineHeight: 110,
    color: NUMERAL_BEIGE,
  },
  stepCol: {
    flex: 1,
    minWidth: 0,
  },
  navRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    marginTop: 24,
  },
  prevButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    height: 52,
    paddingHorizontal: 22,
    borderRadius: PaprikaRadius.lg,
    backgroundColor: Paprika.cardBg,
    borderWidth: 1,
    borderColor: Paprika.border,
    ...PaprikaShadows.card,
  },
  prevButtonText: {
    fontSize: 15,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
  nextButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    height: 52,
    paddingHorizontal: 26,
    borderRadius: PaprikaRadius.lg,
    backgroundColor: Paprika.accent,
  },
  nextButtonText: {
    fontSize: 15,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.onPrimary,
  },
  navDisabled: {
    opacity: 0.45,
  },

  // Mobile
  mobileDots: {
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 12,
  },
  mobileScroll: {
    flex: 1,
  },
  mobileScrollContent: {
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 32,
  },
  mobileNumeralRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
    marginBottom: 10,
  },
  numeralMobile: {
    fontFamily: 'Newsreader-Italic',
    fontStyle: 'italic',
    fontSize: 60,
    lineHeight: 56,
    color: NUMERAL_BEIGE,
  },
  mobileEyebrowBlock: {
    paddingBottom: 6,
  },
  mobileSectionSub: {
    fontSize: 12.5,
    fontFamily: 'Inter-Medium',
    fontWeight: '500',
    color: Paprika.textSecondary,
    marginTop: 2,
  },
  mobileFooterSafe: {
    backgroundColor: Paprika.background,
    borderTopWidth: 1,
    borderTopColor: Paprika.hairline,
  },
  mobileFooter: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 18,
  },
  prevSquare: {
    width: 52,
    height: 52,
    borderRadius: PaprikaRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.cardBg,
    borderWidth: 1,
    borderColor: Paprika.border,
    ...PaprikaShadows.card,
  },
  nextButtonMobile: {
    flex: 1,
  },
});
